/**
 * Simulation Overlay for Emulated Soundworks Clients
 * Provides manual controls for stress testing scroll logic.
 */

const GLOBAL_REGISTRY = '__SW_SIM_CLIENTS__';

// Use window.top for the registry so ONE loop can control ALL frames
// Fallback to window if top is inaccessible (CORS)
let registryScope;
try {
    registryScope = window.top;
    if (!registryScope[GLOBAL_REGISTRY]) {
        registryScope[GLOBAL_REGISTRY] = [];
    }
} catch (e) {
    registryScope = window;
    if (!registryScope[GLOBAL_REGISTRY]) {
        registryScope[GLOBAL_REGISTRY] = [];
    }
}

let isRunning = false;
let rafId = null;

// Simulation State
const simState = {
    baseSpeed: 50,
    spread: 15,
    jitter: 0
};

export function initSimulation(container) {
    // Always register client to Global Scope (so it CAN be controlled)
    const staticRandom = Math.random() - 0.5;

    registryScope[GLOBAL_REGISTRY].push({
        el: container,
        staticRandom: staticRandom,
        active: true
    });

    // Initialize UI (Conditional)
    // - Must have '?sim=true' in URL
    // - If emulated (iframe), only show on Index 0 to prevent duplicates
    const params = new URLSearchParams(window.location.search);
    const isSim = params.has('sim');
    const isLeader = (window === window.top) || (params.get('index') === '0');

    if (isSim && isLeader && !document.getElementById('sim-overlay-root')) {
        createOverlayUI();
    }
}

function createOverlayUI() {
    const root = document.createElement('div');
    root.id = 'sim-overlay-root';

    // Panel Styles
    Object.assign(root.style, {
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        zIndex: '99999',
        background: 'rgba(0, 0, 0, 0.85)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '12px',
        padding: '16px',
        fontFamily: 'monospace',
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        width: '220px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)'
    });

    const btn = document.createElement('button');
    btn.id = 'sim-overlay-btn';
    btn.innerText = '▶ START SIM';
    Object.assign(btn.style, {
        padding: '10px',
        background: '#ff0055',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        fontWeight: 'bold',
        cursor: 'pointer',
        textTransform: 'uppercase'
    });

    btn.onclick = () => {
        setSimRunning(!isRunning);
    };

    // 2. Sliders
    const createSlider = (label, key, min, max) => {
        const row = document.createElement('div');
        row.style.display = 'flex';
        row.style.flexDirection = 'column';
        row.style.marginBottom = '4px';

        const header = document.createElement('div');
        header.style.display = 'flex';
        header.style.justifyContent = 'space-between';
        header.style.fontSize = '11px';
        header.style.color = '#aaa';

        const labelSpan = document.createElement('span');
        labelSpan.innerText = label;

        const valSpan = document.createElement('span');
        valSpan.innerText = simState[key];
        valSpan.style.color = '#00ff88';

        const input = document.createElement('input');
        input.type = 'range';
        input.min = min;
        input.max = max;
        input.value = simState[key];
        input.style.width = '100%';
        input.style.marginTop = '4px';
        input.style.cursor = 'grab';

        input.oninput = (e) => {
            const v = parseFloat(e.target.value);
            simState[key] = v;
            valSpan.innerText = v;
        };

        header.appendChild(labelSpan);
        header.appendChild(valSpan);
        row.appendChild(header);
        row.appendChild(input);
        return row;
    };

    root.appendChild(btn);
    root.appendChild(createSlider('BASE SPEED', 'baseSpeed', 0, 100));
    root.appendChild(createSlider('SPREAD (Diff)', 'spread', 0, 100));
    root.appendChild(createSlider('JITTER (Chaos)', 'jitter', 0, 50));

    document.body.appendChild(root);
}

export function setSimRunning(val) {
    if (val === isRunning) return;
    isRunning = val;
    const btn = document.getElementById('sim-overlay-btn');
    if (isRunning) {
        if (btn) {
            btn.innerText = '⏹ STOP SIM';
            btn.style.background = '#00ff88';
            btn.style.color = 'black';
        }
        startLoop();
    } else {
        if (btn) {
            btn.innerText = '▶ START SIM';
            btn.style.background = '#ff0055';
            btn.style.color = 'white';
        }
        stopLoop();
    }
}

function startLoop() {
    function step() {
        if (!isRunning) return;

        // Drive ALL clients found in the registry scope (Top or Local)
        const targets = registryScope[GLOBAL_REGISTRY];

        targets.forEach(client => {
            if (client.el.simulateScroll) {
                // Calculate Speed
                // Speed = Base + (UniqueOffset * Spread) + (FrameRandom * Jitter)
                const frameRandom = Math.random() - 0.5;

                let speed = (simState.baseSpeed
                    + (client.staticRandom * simState.spread)
                    + (frameRandom * simState.jitter)) * 0.15; // Scale: Slider 100 = Max Human Speed

                client.el.simulateScroll(speed);
            }
        });

        rafId = requestAnimationFrame(step);
    }

    rafId = requestAnimationFrame(step);
}

function stopLoop() {
    if (rafId) cancelAnimationFrame(rafId);
}
